import { apiClient } from '../../../lib/api/client';
import type {
  CreateQuotePayload,
  GetQuotesParams,
  PagedResult,
  QuoteDetails,
  QuoteListItem,
} from '../types';

export async function fetchQuotes(
  params: GetQuotesParams = {}
): Promise<PagedResult<QuoteListItem>> {
  const {
    pageNumber = 1,
    pageSize = 10,
    search,
    status,
    leadId,
  } = params;

  const searchParams = new URLSearchParams();
  searchParams.set('pageNumber', String(pageNumber));
  searchParams.set('pageSize', String(pageSize));
  if (search !== undefined && search !== '') searchParams.set('search', search);
  if (status !== undefined) searchParams.set('status', String(status));
  if (leadId !== undefined && leadId !== '') searchParams.set('leadId', leadId);

  const response = await apiClient.get<PagedResult<QuoteListItem>>(
    `/quotes?${searchParams.toString()}`
  );
  return response.data;
}

export async function createQuote(payload: CreateQuotePayload): Promise<{ id: string }> {
  const formData = new FormData();
  if (payload.leadId != null) formData.append('leadId', payload.leadId);
  formData.append('title', payload.title);
  if (payload.description !== undefined && payload.description !== '') {
    formData.append('description', payload.description);
  }
  for (const file of payload.files) {
    formData.append('files', file, file.name);
  }

  const response = await apiClient.post<{ id: string }>('/quotes', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
}

export async function getQuoteById(id: string): Promise<QuoteDetails> {
  const response = await apiClient.get<QuoteDetails>(`/quotes/${id}`);
  return response.data;
}

export async function updateQuote(
  id: string,
  payload: {
    title: string;
    description: string | null;
    leadId: string | null;
    validUntilDate?: string | null;
  }
): Promise<void> {
  await apiClient.put(`/quotes/${id}`, {
    title: payload.title,
    description: payload.description,
    leadId: payload.leadId,
    validUntilDate: payload.validUntilDate ?? null,
  });
}

export async function sendQuote(id: string): Promise<void> {
  await apiClient.post(`/quotes/${id}/send`);
}

export async function acceptQuote(id: string, expiryDate?: string | null): Promise<void> {
  await apiClient.post(`/quotes/${id}/accept`, { expiryDate: expiryDate ?? null });
}

export async function rejectQuote(id: string, reason?: string): Promise<void> {
  await apiClient.post(`/quotes/${id}/reject`, { reason: reason ?? null });
}

export async function cancelQuote(id: string, reason?: string): Promise<void> {
  await apiClient.post(`/quotes/${id}/cancel`, { reason: reason ?? null });
}

export async function requestRevision(id: string, revisionNote?: string): Promise<void> {
  await apiClient.post(`/quotes/${id}/request-revision`, { revisionNote: revisionNote ?? null });
}

export async function expireQuote(id: string): Promise<void> {
  await apiClient.post(`/quotes/${id}/expire`);
}

export async function deleteQuote(id: string): Promise<void> {
  await apiClient.delete(`/quotes/${id}`);
}

export async function downloadQuoteFile(quoteId: string, blobId: string): Promise<void> {
  const response = await apiClient.get<ArrayBuffer>(
    `/quotes/${quoteId}/files/${blobId}`,
    {
      responseType: 'arraybuffer',
    }
  );

  const contentType =
    response.headers['content-type'] ?? 'application/octet-stream';
  const blob = new Blob([response.data], { type: contentType });

  const disposition = response.headers['content-disposition'] as
    | string
    | undefined;
  let filename = 'file';
  if (disposition) {
    const match = /filename="?([^"]+)"?/.exec(disposition);
    if (match && match[1]) {
      filename = decodeURIComponent(match[1]);
    }
  }

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

